/**
 * Client-side end-to-end encryption for media, built on libsodium.
 *
 * Design (portable to iOS swift-sodium / Android lazysodium):
 *   - Key derivation: Argon2id (crypto_pwhash) from the user's password + a
 *     per-user salt. The salt + ops/mem cost params live server-side (not
 *     secret) so any device re-derives the same key after the user re-enters
 *     their password. The password + derived key NEVER leave the device.
 *   - Per-file encryption: XSalsa20-Poly1305 (crypto_secretbox_easy) with a
 *     fresh random nonce per file.
 *   - Stored blob layout:  magic(4 "BSE1") | version(1) | nonce(24) | ciphertext
 *   - Verifier: secretbox(VERIFIER_PLAINTEXT, nonce, key) stored server-side so
 *     a new device can confirm a typed password is correct before using it.
 */
import type _sodium from 'libsodium-wrappers-sumo';

export type Sodium = typeof _sodium;

let ready: Promise<Sodium> | null = null;

/**
 * Resolve (and memoise) the initialised libsodium instance. We use the *sumo*
 * build because the slim `libsodium-wrappers` omits crypto_pwhash (Argon2id),
 * which we rely on for password-based key derivation. Loaded via dynamic import
 * so its WASM/JS lands in its own chunk — out of the initial/onboarding bundle
 * until encryption is actually needed.
 */
export function initCrypto(): Promise<Sodium> {
  if (!ready) {
    ready = import('libsodium-wrappers-sumo').then(async (mod) => {
      const s = (mod.default ?? mod) as Sodium;
      await s.ready;
      return s;
    });
  }
  return ready;
}

/**
 * Build a Blob from bytes with a guaranteed plain-ArrayBuffer backing. libsodium
 * returns Uint8Array<ArrayBufferLike>, which the DOM Blob types reject directly;
 * copying into a fresh ArrayBuffer keeps it type-safe and detaches from the WASM heap.
 */
export function bytesToBlob(bytes: Uint8Array, type: string): Blob {
  const ab = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(ab).set(bytes);
  return new Blob([ab], { type });
}

/* ── Blob framing ───────────────────────────────────────────────────── */

const MAGIC = new Uint8Array([0x42, 0x53, 0x45, 0x31]); // "BSE1"
const VERSION = 1;

/** Plaintext encrypted into the verifier — fixed & well-known (not a secret). */
const VERIFIER_PLAINTEXT = 'babysteps-e2ee-verify-v1';

/* ── KDF params (interactive: snappy on phones, still strong) ────────── */

export interface KdfParams {
  salt_b64: string;
  ops: number;
  mem: number;
  algo: string; // informational; we always use Argon2id
}

export function newSalt(s: Sodium): string {
  return s.to_base64(s.randombytes_buf(s.crypto_pwhash_SALTBYTES), s.base64_variants.ORIGINAL);
}

export function defaultKdfParams(s: Sodium, salt_b64: string): KdfParams {
  return {
    salt_b64,
    ops: s.crypto_pwhash_OPSLIMIT_INTERACTIVE,
    mem: s.crypto_pwhash_MEMLIMIT_INTERACTIVE,
    algo: 'argon2id',
  };
}

/** Derive a 32-byte secretbox key from a password + KDF params. */
export function deriveKey(s: Sodium, password: string, params: KdfParams): Uint8Array {
  const salt = s.from_base64(params.salt_b64, s.base64_variants.ORIGINAL);
  return s.crypto_pwhash(
    s.crypto_secretbox_KEYBYTES,
    password,
    salt,
    params.ops,
    params.mem,
    s.crypto_pwhash_ALG_ARGON2ID13,
  );
}

/* ── Verifier ───────────────────────────────────────────────────────── */

export interface Verifier {
  nonce_b64: string;
  cipher_b64: string;
}

export function makeVerifier(s: Sodium, key: Uint8Array): Verifier {
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const cipher = s.crypto_secretbox_easy(s.from_string(VERIFIER_PLAINTEXT), nonce, key);
  return {
    nonce_b64: s.to_base64(nonce, s.base64_variants.ORIGINAL),
    cipher_b64: s.to_base64(cipher, s.base64_variants.ORIGINAL),
  };
}

/** True when `key` correctly opens the stored verifier (i.e. password is right). */
export function checkVerifier(s: Sodium, key: Uint8Array, v: Verifier): boolean {
  try {
    const nonce = s.from_base64(v.nonce_b64, s.base64_variants.ORIGINAL);
    const cipher = s.from_base64(v.cipher_b64, s.base64_variants.ORIGINAL);
    const opened = s.crypto_secretbox_open_easy(cipher, nonce, key);
    return s.to_string(opened) === VERIFIER_PLAINTEXT;
  } catch {
    return false;
  }
}

/* ── File encryption ────────────────────────────────────────────────── */

/** Encrypt bytes → framed blob (magic|version|nonce|ciphertext). */
export function encryptBytes(s: Sodium, key: Uint8Array, plaintext: Uint8Array): Uint8Array {
  const nonce = s.randombytes_buf(s.crypto_secretbox_NONCEBYTES);
  const cipher = s.crypto_secretbox_easy(plaintext, nonce, key);
  const out = new Uint8Array(MAGIC.length + 1 + nonce.length + cipher.length);
  let o = 0;
  out.set(MAGIC, o); o += MAGIC.length;
  out[o] = VERSION; o += 1;
  out.set(nonce, o); o += nonce.length;
  out.set(cipher, o);
  return out;
}

/** Parse + decrypt a framed blob. Throws on bad magic/version or auth failure. */
export function decryptBytes(s: Sodium, key: Uint8Array, blob: Uint8Array): Uint8Array {
  const nonceLen = s.crypto_secretbox_NONCEBYTES;
  const headerLen = MAGIC.length + 1 + nonceLen;
  if (blob.length < headerLen) {
    throw new Error('Encrypted blob too short.');
  }
  for (let i = 0; i < MAGIC.length; i++) {
    if (blob[i] !== MAGIC[i]) throw new Error('Not a BabyNook encrypted blob.');
  }
  if (blob[MAGIC.length] !== VERSION) {
    throw new Error(`Unsupported encryption version ${blob[MAGIC.length]}.`);
  }
  const nonce = blob.slice(MAGIC.length + 1, headerLen);
  const cipher = blob.slice(headerLen);
  return s.crypto_secretbox_open_easy(cipher, nonce, key);
}
